import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isSinger, compatibilityToLabel, ENERGY_OPTIONS } from '../utils/singerFriendly';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Sparkles, 
  Wand2, 
  Music,
  ListMusic,
  Plus,
  ArrowRight,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MUSIC_KEYS = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];
const THEMES = ['Adoración', 'Alabanza', 'Avivamiento', 'Gratitud', 'Fe', 'Esperanza', 'Amor', 'Gracia', 'Salvación', 'Victoria'];

export const GeneratorPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const singerMode = isSinger(user);
  
  // Popurrí Generator State
  const [theme, setTheme] = useState('');
  const [targetKey, setTargetKey] = useState('G');
  const [songCount, setSongCount] = useState(4);
  const [energy, setEnergy] = useState('moderate');
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  
  // Lyrics Classifier State
  const [lyrics, setLyrics] = useState('');
  const [classifying, setClassifying] = useState(false);
  const [classification, setClassification] = useState(null);
  
  // Create Medley State
  const [medleyTitle, setMedleyTitle] = useState('');
  const [creatingMedley, setCreatingMedley] = useState(false);

  const handleGeneratePopurri = async () => {
    if (!theme) {
      toast.error('Por favor ingresa un tema');
      return;
    }
    
    setGenerating(true);
    setSuggestions(null);
    
    try {
      const response = await fetch(`${API}/ai/generate-popurri`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(singerMode
          ? { theme, key: null, count: ENERGY_OPTIONS.find(o => o.value === energy)?.count || 4, energy }
          : { theme, key: targetKey, count: songCount }
        )
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        toast.success('Popurrí generado exitosamente');
      } else {
        throw new Error('Generation failed');
      }
    } catch (error) {
      toast.error('Error al generar el popurrí');
    } finally {
      setGenerating(false);
    }
  };

  const handleClassifyLyrics = async () => {
    if (!lyrics.trim()) {
      toast.error('Por favor ingresa la letra');
      return;
    }
    
    setClassifying(true);
    setClassification(null);
    
    try {
      const response = await fetch(`${API}/ai/classify-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ lyrics })
      });
      
      if (response.ok) {
        const data = await response.json();
        setClassification(data);
        toast.success('Letra clasificada exitosamente');
      } else {
        throw new Error('Classification failed');
      }
    } catch (error) {
      toast.error('Error al clasificar la letra');
    } finally {
      setClassifying(false);
    }
  };

  const handleCreateMedley = async () => {
    if (!suggestions || !medleyTitle) {
      toast.error('Por favor genera un popurrí y asigna un título');
      return;
    }
    
    setCreatingMedley(true);
    
    try {
      const songIds = suggestions.suggested_songs.map(s => s.song_id);
      
      const response = await fetch(`${API}/medleys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          title: medleyTitle,
          song_ids: songIds,
          global_key: targetKey,
          is_public: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Medley creado exitosamente');
        navigate(`/medleys/${data.medley_id}`);
      } else {
        throw new Error('Creation failed');
      }
    } catch (error) {
      toast.error('Error al crear el medley');
    } finally {
      setCreatingMedley(false);
    }
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '');
    return keyMap[baseKey] || 'bg-primary';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="generator-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-accent" />
          Generador con IA
        </h1>
        <p className="text-muted-foreground mt-1">
          Crea popurrís inteligentes y clasifica letras automáticamente
        </p>
      </div>

      <Tabs defaultValue="popurri" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="popurri" data-testid="tab-popurri">Generar Popurrí</TabsTrigger>
          <TabsTrigger value="classify" data-testid="tab-classify">Clasificar Letra</TabsTrigger>
        </TabsList>

        {/* Popurrí Generator */}
        <TabsContent value="popurri" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-accent" />
                  Configuración del Popurrí
                </CardTitle>
                <CardDescription>
                  La IA seleccionará canciones que fluyan armónicamente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tema del Popurrí *</Label>
                  <Select value={theme} onValueChange={setTheme}>
                    <SelectTrigger data-testid="theme-select">
                      <SelectValue placeholder="Selecciona un tema" />
                    </SelectTrigger>
                    <SelectContent>
                      {THEMES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    O escribe un tema personalizado:
                  </p>
                  <Input
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    placeholder="Ej: Renovación espiritual"
                    data-testid="theme-input"
                  />
                </div>

                {singerMode ? (
                  <div className="space-y-2">
                    <Label>Energía del momento</Label>
                    <Select value={energy} onValueChange={setEnergy}>
                      <SelectTrigger data-testid="energy-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENERGY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label} ({opt.count} canciones)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tonalidad objetivo</Label>
                      <Select value={targetKey} onValueChange={setTargetKey}>
                        <SelectTrigger data-testid="key-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MUSIC_KEYS.map(k => (
                            <SelectItem key={k} value={k}>{k} Mayor</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Cantidad de canciones</Label>
                      <Select value={songCount.toString()} onValueChange={(v) => setSongCount(parseInt(v))}>
                        <SelectTrigger data-testid="count-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[3, 4, 5, 6].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n} canciones</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleGeneratePopurri}
                  disabled={generating || !theme}
                  className="w-full h-12"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generar Popurrí
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ListMusic className="w-5 h-5" />
                  Resultado
                </CardTitle>
              </CardHeader>
              <CardContent>
                {suggestions ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Tema: {suggestions.theme}</p>
                        {!singerMode && (
                          <p className="text-sm text-muted-foreground">Tonalidad: {suggestions.target_key}</p>
                        )}
                      </div>
                      {!singerMode && (
                        <Badge className={getKeyBadgeClass(suggestions.target_key)}>
                          {suggestions.target_key}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {suggestions.suggested_songs.map((song, index) => (
                        <div
                          key={song.song_id}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                          data-testid={`suggestion-${index}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{song.title}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {!singerMode && (
                                <Badge variant="outline" className="text-xs">
                                  {song.original_key}
                                </Badge>
                              )}
                              {song.theme && <span>{song.theme}</span>}
                            </div>
                          </div>
                          {song.key_compatibility && (
                            singerMode ? (
                              <Badge className={`text-xs ${compatibilityToLabel(song.key_compatibility.level).color}`}>
                                {compatibilityToLabel(song.key_compatibility.level).text}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                {song.key_compatibility.level}
                              </Badge>
                            )
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Create Medley */}
                    <div className="border-t pt-4 mt-4">
                      <Label className="mb-2 block">Guardar como Medley</Label>
                      <div className="flex gap-2">
                        <Input
                          value={medleyTitle}
                          onChange={(e) => setMedleyTitle(e.target.value)}
                          placeholder="Nombre del medley"
                          data-testid="medley-title-input"
                        />
                        <Button
                          onClick={handleCreateMedley}
                          disabled={creatingMedley || !medleyTitle}
                          data-testid="create-medley-btn"
                        >
                          {creatingMedley ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Music className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Configura los parámetros y genera tu popurrí
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Lyrics Classifier */}
        <TabsContent value="classify" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Input */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle>Analizar Letra</CardTitle>
                <CardDescription>
                  Pega la letra de una canción para clasificarla automáticamente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={lyrics}
                  onChange={(e) => setLyrics(e.target.value)}
                  placeholder="Pega aquí la letra de la canción..."
                  rows={12}
                  data-testid="lyrics-input"
                />
                <Button
                  onClick={handleClassifyLyrics}
                  disabled={classifying || !lyrics.trim()}
                  className="w-full"
                  data-testid="classify-btn"
                >
                  {classifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analizando...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Clasificar con IA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle>Resultado del Análisis</CardTitle>
              </CardHeader>
              <CardContent>
                {classification ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <span className="font-medium">Análisis completado</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Tema</p>
                        <p className="font-semibold text-lg">{classification.theme}</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Tonalidad sugerida</p>
                        <Badge className={`${getKeyBadgeClass(classification.suggested_key)} text-lg px-3 py-1`}>
                          {classification.suggested_key}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Estado de ánimo</p>
                      <p className="font-medium">{classification.mood}</p>
                    </div>
                    
                    {classification.tags?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Etiquetas sugeridas</p>
                        <div className="flex flex-wrap gap-2">
                          {classification.tags.map((tag, i) => (
                            <Badge key={i} variant="outline">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        // Pre-fill new song form
                        navigate('/songs?new=true');
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear canción con estos datos
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Wand2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Pega una letra para analizarla
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GeneratorPage;
